import { AppliedFilterInterface, ChoiceList, FilterInterface, Select } from '@shopify/polaris'
import { useCallback } from 'react'

import { SubscriptionFilter } from '../api'

type Frequency = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'ANCHORS'

const INTERVAL_UNIT: Record<Exclude<Frequency, 'ANCHORS'>, string> = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
}

const INTERVAL_COUNT_MAX = 12

const intervalCountOptions = (frequency: Exclude<Frequency, 'ANCHORS'>) => {
  const unit = INTERVAL_UNIT[frequency]
  return Array.from({ length: INTERVAL_COUNT_MAX }, (_, i) => {
    const n = i + 1
    return {
      value: String(n),
      label: n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`,
    }
  })
}

const frequencyLabel = (frequency: Frequency, count?: number): string => {
  if (frequency === 'ANCHORS') {
    return 'on multiple anchors'
  }
  const unit = INTERVAL_UNIT[frequency]
  const n = count ?? 1
  return n === 1 ? `every ${unit}` : `every ${n} ${unit}s`
}

export const useAddFrequencyFilter = (
  filter: SubscriptionFilter,
  updateFilter: (newFilter: Partial<SubscriptionFilter>) => any,
  filters: FilterInterface[],
  appliedFilters: AppliedFilterInterface[]
) => {
  const frequency = filter.frequency as Frequency | undefined

  const setFrequency = useCallback(
    (sel: string[]) => {
      const next = sel[0] as Frequency | undefined
      updateFilter({
        frequency: next,
        frequency_count: next === 'ANCHORS' ? undefined : filter.frequency_count,
      })
    },
    [updateFilter, filter.frequency_count]
  )

  const setIntervalCount = useCallback(
    (value: string) => {
      const parsed = parseInt(value)
      updateFilter({
        frequency_count: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined,
      })
    },
    [updateFilter]
  )

  const renderIntervalCount = (unit: Exclude<Frequency, 'ANCHORS'>) =>
    function renderIntervalCountForUnit(selected: boolean) {
      if (!selected) {
        return null
      }
      return (
        <Select
          label="Interval"
          labelHidden
          options={intervalCountOptions(unit)}
          value={String(filter.frequency_count ?? 1)}
          onChange={setIntervalCount}
        />
      )
    }

  const choices = [
    { value: 'DAY', label: 'Daily', renderChildren: renderIntervalCount('DAY') },
    { value: 'WEEK', label: 'Weekly', renderChildren: renderIntervalCount('WEEK') },
    { value: 'MONTH', label: 'Monthly', renderChildren: renderIntervalCount('MONTH') },
    { value: 'YEAR', label: 'Yearly', renderChildren: renderIntervalCount('YEAR') },
    { value: 'ANCHORS', label: 'On multiple anchors' },
  ]

  filters.push({
    key: 'frequency',
    label: 'Billing frequency',
    filter: (
      <ChoiceList
        title="Billing frequency"
        titleHidden
        choices={choices}
        selected={frequency ? [frequency] : []}
        onChange={setFrequency}
      />
    ),
  })

  if (frequency) {
    appliedFilters.push({
      key: 'frequency',
      label: `Billing frequency: ${frequencyLabel(frequency, filter.frequency_count)}`,
      onRemove: () => updateFilter({ frequency: undefined, frequency_count: undefined }),
    })
  }
}
